import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import { getState, getFarmerById, getFarmerKyc, getNotifications, getNotificationsByFarmer, getLoansByFarmer, getDebtsByFarmer } from '@/store';
import { formatCurrencyCompact } from '@/utils/format';
import { EmptyState } from '@/components/base/EmptyState';

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'danger' | 'info' | 'ekyc' | 'payment' | 'reminder';
  title: string;
  message: string;
  time: string;
  read: boolean;
  debtId?: string;
}

export default function FarmerNotificationsPage() {
  useStore();
  const { user } = useAuth();

  const state = getState();
  const farmerId = user?.farmerId || 'f1';
  const farmer = getFarmerById(farmerId);
  const kyc = farmer ? getFarmerKyc(farmer) : null;
  const [filter, setFilter] = useState('all');

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const notifications = useMemo<Notification[]>(() => {
    const notifs: Notification[] = [];
    const loans = getLoansByFarmer(farmerId);
    const debts = getDebtsByFarmer(farmerId);

    // ── Read actual store notifications first ──
    const storeNotifications = getNotificationsByFarmer(farmerId);
    storeNotifications.forEach((sn) => {
      let type: Notification['type'] = 'info';
      if (sn.type === 'debt_reminder') type = 'reminder';
      else if (sn.type === 'debt_overdue') type = 'danger';
      else if (sn.type === 'debt_due') type = 'warning';
      else if (sn.type === 'disburse_success' || sn.type === 'loan_approved') type = 'success';
      else if (sn.type === 'disburse_failed' || sn.type === 'loan_rejected') type = 'danger';

      notifs.push({
        id: sn.id,
        type,
        title: sn.title,
        message: sn.content,
        time: sn.ngayTao,
        read: sn.daDoc,
        debtId: sn.debtId,
      });
    });

    // ── Computed notifications from data (ekyc, loan status, debt status) ──
    // eKYC status notification
    if (kyc) {
      const alreadyHasEkycNotif = notifs.some((n) => n.id.startsWith('ekyc-'));
      if (!alreadyHasEkycNotif) {
        if (kyc.kycStatus === 'ekyc_verified' && kyc.verifiedAt) {
          notifs.push({
            id: 'ekyc-verified', type: 'success', title: 'eKYC xác thực thành công',
            message: `Tài khoản của bạn đã được xác thực. Credit Score: ${kyc.creditScore || '-'}. Risk Level: ${kyc.riskLevel || '-'}.`,
            time: kyc.verifiedAt, read: false,
          });
        }
        if (kyc.kycStatus === 'ekyc_need_more') {
          notifs.push({
            id: 'ekyc-needmore', type: 'ekyc', title: 'eKYC cần bổ sung thông tin',
            message: kyc.kycReason || 'Vui lòng bổ sung thông tin theo yêu cầu để hoàn tất xác thực.',
            time: new Date().toISOString(), read: false,
          });
        }
        if (kyc.kycStatus === 'ekyc_failed') {
          notifs.push({
            id: 'ekyc-failed', type: 'danger', title: 'eKYC thất bại',
            message: kyc.kycReason || 'Xác thực không thành công. Vui lòng thử lại.',
            time: new Date().toISOString(), read: false,
          });
        }
      }
    }

    // Limit granted
    const limits = state.limits.filter((l) => l.farmerId === farmerId);
    limits.forEach((l) => {
      notifs.push({
        id: `limit-${l.id}`, type: 'info', title: 'Hạn mức được cấp',
        message: `Bạn được cấp hạn mức ${l.loaiDinhMuc}: ${formatCurrencyCompact(l.hanMuc)} cho năm ${l.nam}.`,
        time: `2025-01-01T00:00:00`, read: true,
      });
    });

    // Loan approved - only if no store notification already covers it
    loans.filter((l) => ['Đã duyệt', 'Chờ giải ngân', 'approved_locked'].includes(l.status)).forEach((l) => {
      if (!notifs.some((n) => n.id === `ln-approved-${l.id}`)) {
        notifs.push({
          id: `ln-approved-${l.id}`, type: 'success', title: 'Yêu cầu vay đã được duyệt',
          message: `Khoản vay ${formatCurrencyCompact(l.soTien)} cho "${l.mucDich}" đã được HTX duyệt và đang chờ giải ngân.`,
          time: l.ngayDuyet || l.ngayYeuCau, read: false,
        });
      }
    });

    // Disbursement success
    loans.filter((l) => l.status === 'Đã giải ngân' || l.status === 'disbursed').forEach((l) => {
      if (!notifs.some((n) => n.id === `ln-disbursed-${l.id}`)) {
        notifs.push({
          id: `ln-disbursed-${l.id}`, type: 'success', title: 'Giải ngân thành công',
          message: `Khoản vay ${formatCurrencyCompact(l.soTien)} đã được giải ngân vào tài khoản của bạn.`,
          time: l.ngayDuyet || l.ngayYeuCau, read: false,
        });
      }
    });

    // Disbursement error
    loans.filter((l) => l.status === 'Giải ngân lỗi').forEach((l) => {
      if (!notifs.some((n) => n.id === `ln-disberr-${l.id}`)) {
        notifs.push({
          id: `ln-disberr-${l.id}`, type: 'danger', title: 'Giải ngân thất bại',
          message: `Khoản vay ${formatCurrencyCompact(l.soTien)} giải ngân lỗi: ${l.lyDoTuChoi || 'Vui lòng kiểm tra lại thông tin tài khoản.'}`,
          time: l.ngayDuyet || l.ngayYeuCau, read: false,
        });
      }
    });

    // Loan rejected
    loans.filter((l) => l.status === 'Từ chối').forEach((l) => {
      if (!notifs.some((n) => n.id === `ln-rejected-${l.id}`)) {
        notifs.push({
          id: `ln-rejected-${l.id}`, type: 'danger', title: 'Yêu cầu vay bị từ chối',
          message: l.lyDoTuChoi || `Khoản vay ${formatCurrencyCompact(l.soTien)} cho "${l.mucDich}" đã bị từ chối.`,
          time: l.ngayDuyet || l.ngayYeuCau, read: false,
        });
      }
    });

    // Loan need more
    loans.filter((l) => l.status === 'Cần bổ sung').forEach((l) => {
      if (!notifs.some((n) => n.id === `ln-needmore-${l.id}`)) {
        notifs.push({
          id: `ln-needmore-${l.id}`, type: 'warning', title: 'Yêu cầu vay cần bổ sung',
          message: l.noiDungCanBoSung || `Yêu cầu ${l.id} cần bổ sung thông tin. Vui lòng kiểm tra chi tiết.`,
          time: l.ngayYeuCau, read: false,
        });
      }
    });

    // Due soon
    debts.filter((d) => d.status === 'Sắp đến hạn' || d.status === 'Đến hạn').forEach((d) => {
      if (!notifs.some((n) => n.id === `debt-due-${d.id}`)) {
        notifs.push({
          id: `debt-due-${d.id}`, type: 'warning', title: 'Khoản vay sắp đến hạn',
          message: `Khoản ${d.maKhoanVay || d.id} còn nợ ${formatCurrencyCompact(d.conNo)}, hạn trả: ${d.ngayDenHan}. Vui lòng chuẩn bị thanh toán.`,
          time: d.ngayDenHan, read: false,
        });
      }
    });

    // Overdue
    debts.filter((d) => d.status === 'Quá hạn').forEach((d) => {
      if (!notifs.some((n) => n.id === `debt-overdue-${d.id}`)) {
        notifs.push({
          id: `debt-overdue-${d.id}`, type: 'danger', title: 'Khoản nợ quá hạn',
          message: `Khoản ${d.maKhoanVay || d.id} còn nợ ${formatCurrencyCompact(d.conNo)} đã quá hạn từ ${d.ngayDenHan}. Vui lòng thanh toán ngay để tránh ảnh hưởng credit score.`,
          time: d.ngayDenHan, read: false,
        });
      }
    });

    // Settlement complete
    debts.filter((d) => d.status === 'Đã tất toán').forEach((d) => {
      if (!notifs.some((n) => n.id === `debt-settled-${d.id}`)) {
        notifs.push({
          id: `debt-settled-${d.id}`, type: 'success', title: 'Tất toán thành công',
          message: `Khoản vay ${d.maKhoanVay || d.id} đã được tất toán. Cảm ơn bạn đã thanh toán đúng hạn!`,
          time: new Date().toISOString(), read: true,
        });
      }
    });

    // Payment error
    debts.filter((d) => d.status === 'Chờ xử lý lỗi thanh toán' || d.status === 'Thu nợ lỗi').forEach((d) => {
      if (!notifs.some((n) => n.id === `debt-payerr-${d.id}`)) {
        notifs.push({
          id: `debt-payerr-${d.id}`, type: 'danger', title: 'Lỗi thanh toán',
          message: `Khoản ${d.maKhoanVay || d.id}: Có lỗi trong quá trình thanh toán. Vui lòng thử lại hoặc liên hệ HTX.`,
          time: d.ngayDenHan, read: false,
        });
      }
    });

    // Sort by time desc
    notifs.sort((a, b) => b.time.localeCompare(a.time));
    return notifs;
  }, [state, farmerId, kyc]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filterOptions = [
    { key: 'all', label: 'Tất cả', count: notifications.length },
    { key: 'unread', label: 'Chưa đọc', count: unreadCount },
    { key: 'reminder', label: 'Nhắc trả', count: notifications.filter((n) => n.type === 'reminder').length },
    { key: 'success', label: 'Thành công', count: notifications.filter((n) => n.type === 'success').length },
    { key: 'warning', label: 'Cảnh báo', count: notifications.filter((n) => n.type === 'warning').length },
    { key: 'danger', label: 'Nguy hiểm', count: notifications.filter((n) => n.type === 'danger').length },
  ];

  const filteredNotifs = filter === 'all'
    ? notifications
    : filter === 'unread'
    ? notifications.filter((n) => !n.read)
    : notifications.filter((n) => n.type === filter);

  return (
    <div className="pb-4 max-w-[800px] mx-auto">
      <div className="mx-4 mt-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Thông báo</h2>
            {unreadCount > 0 && (
              <p className="text-[10px] text-slate-400 mt-0.5">{unreadCount} thông báo chưa đọc</p>
            )}
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1">
          {filterOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setFilter(opt.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors ${
                filter === opt.key
                  ? 'bg-agrida-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {opt.label}
              {opt.count > 0 && (
                <span className={`ml-1 text-[9px] ${filter === opt.key ? 'text-white/70' : 'text-slate-400'}`}>
                  {opt.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {filteredNotifs.length === 0 ? (
          <EmptyState
            icon="ri-notification-off-line"
            title="Không có thông báo"
            description={filter === 'unread' ? 'Tất cả thông báo đã được đọc' : 'Chưa có thông báo nào'}
          />
        ) : (
          <div className="space-y-2">
            {filteredNotifs.map((n) => {
              const typeConfig: Record<string, { bg: string; iconBg: string; icon: string; dot: string }> = {
                success: { bg: 'bg-agrida-50 border-agrida-200', iconBg: 'bg-agrida-100 text-agrida-600', icon: 'ri-checkbox-circle-line', dot: 'bg-agrida-500' },
                warning: { bg: 'bg-amber-50 border-amber-200', iconBg: 'bg-amber-100 text-amber-600', icon: 'ri-alert-line', dot: 'bg-amber-500' },
                danger: { bg: 'bg-red-50 border-red-200', iconBg: 'bg-red-100 text-red-600', icon: 'ri-close-circle-line', dot: 'bg-red-500' },
                info: { bg: 'bg-sky-50 border-sky-200', iconBg: 'bg-sky-100 text-sky-600', icon: 'ri-information-line', dot: 'bg-sky-500' },
                ekyc: { bg: 'bg-orange-50 border-orange-200', iconBg: 'bg-orange-100 text-orange-600', icon: 'ri-shield-user-line', dot: 'bg-orange-500' },
                payment: { bg: 'bg-violet-50 border-violet-200', iconBg: 'bg-violet-100 text-violet-600', icon: 'ri-qr-code-line', dot: 'bg-violet-500' },
                reminder: { bg: 'bg-amber-50 border-amber-200', iconBg: 'bg-amber-100 text-amber-600', icon: 'ri-notification-3-line', dot: 'bg-amber-500' },
              };
              const cfg = typeConfig[n.type] || typeConfig.info;

              return (
                <div key={n.id} className={`${cfg.bg} border rounded-xl p-3 flex items-start gap-3 transition-colors`}>
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${cfg.iconBg}`}>
                    <i className={`${cfg.icon} text-sm`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-slate-900">{n.title}</p>
                      {!n.read && (
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} shrink-0 animate-pulse-dot`} />
                      )}
                    </div>
                    <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">{n.message}</p>
                    <p className="text-[10px] text-slate-400 mt-1.5">{new Date(n.time).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
